import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { conteoFisicoService, ConteoFisicoDetalleCreate } from '../../services/conteoFisicoService';
import { productoService } from '../../services/productoService';
import { varianteService } from '../../services/varianteService';

interface AgregarDetalleFormProps {
  conteoId: number;
  onClose: () => void;
}

const AgregarDetalleForm = ({ conteoId, onClose }: AgregarDetalleFormProps) => {
  const queryClient = useQueryClient();
  const [productoId, setProductoId] = useState<number | null>(null);
  const [varianteId, setVarianteId] = useState<number | null>(null);

  const { data: productosData } = useQuery(
    'productos',
    () => productoService.getAll(1, 500, undefined, undefined, true)
  );

  const { data: variantesData } = useQuery(
    ['variantes', productoId],
    () => varianteService.getAll(1, 100, productoId),
    { enabled: !!productoId }
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue
  } = useForm<ConteoFisicoDetalleCreate>({
    defaultValues: {
      productoId: 0,
      varianteId: undefined,
      cantidadContada: 0,
      notas: ''
    }
  });

  const agregarDetalleMutation = useMutation(
    (data: ConteoFisicoDetalleCreate) => conteoFisicoService.agregarDetalle(conteoId, data),
    {
      onSuccess: () => {
        toast.success('Producto agregado al conteo');
        queryClient.invalidateQueries(['conteo-fisico', conteoId]);
        onClose();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al agregar producto');
      }
    }
  );

  const onSubmit = (data: ConteoFisicoDetalleCreate) => {
    if (!productoId) {
      toast.error('Selecciona un producto');
      return;
    }
    agregarDetalleMutation.mutate({
      ...data,
      productoId,
      varianteId: varianteId || undefined
    });
  };

  const productos = productosData?.data.productos || [];
  const variantes = variantesData?.data.variantes || [];

  useEffect(() => {
    if (productoId) {
      setValue('productoId', productoId);
      setVarianteId(null);
      setValue('varianteId', undefined);
    }
  }, [productoId, setValue]);

  const productoSeleccionado = productos.find(p => p.id === productoId);
  const tieneVariantes = productoSeleccionado?.tipo === 'producto' && variantes.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">Agregar Producto al Conteo</h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Producto *
            </label>
            <select
              value={productoId || ''}
              onChange={(e) => {
                const id = e.target.value ? Number(e.target.value) : null;
                setProductoId(id);
                setValue('productoId', id || 0);
              }}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Selecciona un producto</option>
              {productos.map((producto) => (
                <option key={producto.id} value={producto.id}>
                  {producto.name} {producto.sku ? `(${producto.sku})` : ''}
                </option>
              ))}
            </select>
            {errors.productoId && (
              <p className="text-red-500 text-xs mt-1">{errors.productoId.message}</p>
            )}
          </div>

          {tieneVariantes && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Variante (opcional)
              </label>
              <select
                value={varianteId || ''}
                onChange={(e) => {
                  const id = e.target.value ? Number(e.target.value) : null;
                  setVarianteId(id);
                  setValue('varianteId', id || undefined);
                }}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Sin variante (producto base)</option>
                {variantes.map((variante) => (
                  <option key={variante.id} value={variante.id}>
                    {variante.name} {variante.sku ? `(${variante.sku})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cantidad Contada *
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              {...register('cantidadContada', {
                required: 'Cantidad contada es requerida',
                min: { value: 0, message: 'Debe ser mayor o igual a 0' },
                valueAsNumber: true
              })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.cantidadContada && (
              <p className="text-red-500 text-xs mt-1">{errors.cantidadContada.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notas (opcional)
            </label>
            <textarea
              {...register('notas')}
              rows={2}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Notas sobre este producto..."
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={agregarDetalleMutation.isLoading || !productoId}
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {agregarDetalleMutation.isLoading ? 'Agregando...' : 'Agregar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AgregarDetalleForm;

